import classNames from 'classnames';
import {isEmpty, map, some} from 'lodash';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Table, TableBody, TableRow, TableCell, CopyText, Icon} from 'components';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getAssignmentName} from 'services';

import {
    ActivityInfo, ParticipantsInfo, FilesInfo, SessionResultInfo, StartDateInfo, EndDateInfo, DurationInfo
} from '../SessionHistoryCell';
import {SessionHistoryViewProps} from '../types';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.SESSION_HISTORY_TABLE;

export const SessionHistoryTable = ({
    sessions = [], users, groups, isLoading, className, downloadingFiles = [], textChatHistoryEnabled,
    audioTranscriptionEnabled, audioVideoTranscriptionEnabled, avatarHistoryEnabled, agentChatbotHistoryEnabled,
    onOpenAuditModal, onOpenActivityMessagesModal, onDownload, onPreview, onOpenNotesModal, onSelectFilePipeline, downloadFileDisabled
}: SessionHistoryViewProps) => {
    const {t} = useTranslation();

    const hasFiles = useMemo(() => some(sessions, (session) => !isEmpty(session.files)), [sessions]);

    const headers = useMemo(() => [
        {children: t(T_KEYS.COLUMNS.ID), className: 'tw-w-12'},
        {children: `${t(T_KEYS.COLUMNS.STARTED)} / ${t(T_KEYS.COLUMNS.ENDED)}`, className: 'tw-w-48'},
        {children: t(T_KEYS.COLUMNS.PARTICIPANTS), className: '-2xl:tw-w-64'},
        {children: t(T_KEYS.COLUMNS.ASSIGNMENT), className: 'xl:tw-w-44 -xl:tw-w-32'},
        {children: t(T_KEYS.COLUMNS.ACTIVITY), className: '-2xl:tw-w-20'},
        {children: t(T_KEYS.COLUMNS.FILES), className: hasFiles ? '-2xl:tw-w-48' : ''},
        {children: t(T_KEYS.COLUMNS.AUDIT), className: 'tw-w-16'},
        {children: t(T_KEYS.COLUMNS.RESULT), className: '-2xl:tw-w-36'},
    ], [t, hasFiles]);

    return (
        <div className={classNames(className, 'tw-flex tw-flex-col tw-overflow-auto')}>
            <Table loading={isLoading} noData={isEmpty(sessions)} headers={headers} className='tw-overflow-x-auto tw-min-w-256'>
                <TableBody className='tw-text-default'>
                    {map(sessions, (session) => (
                        <TableRow key={session.sessionId}>
                            <TableCell>
                                <CopyText
                                    text={session.sessionId}
                                    iconOnly
                                    iconColor='default'
                                    copiedLabelOnly
                                />
                            </TableCell>
                            <TableCell>
                                <div className='tw-flex tw-flex-col tw-gap-1'>
                                    <div><StartDateInfo session={session} /></div>
                                    <div><EndDateInfo session={session} /></div>
                                    <DurationInfo session={session} />
                                </div>
                            </TableCell>
                            <TableCell>
                                <ParticipantsInfo session={session} users={users} groups={groups} showPlatformInfo />
                            </TableCell>
                            <TableCell>
                                {getAssignmentName(groups, session.assignment?.groupId, session.assignment?.subgroupId)}
                            </TableCell>
                            <TableCell>
                                <ActivityInfo
                                    session={session}
                                    avatarHistoryEnabled={avatarHistoryEnabled}
                                    agentChatbotHistoryEnabled={agentChatbotHistoryEnabled}
                                    textChatHistoryEnabled={textChatHistoryEnabled}
                                    audioTranscriptionEnabled={audioTranscriptionEnabled}
                                    audioVideoTranscriptionEnabled={audioVideoTranscriptionEnabled}
                                    onOpenActivityMessagesModal={onOpenActivityMessagesModal}
                                />
                            </TableCell>
                            <TableCell>
                                <FilesInfo
                                    session={session}
                                    downloadingFiles={downloadingFiles}
                                    downloadFileDisabled={downloadFileDisabled}
                                    onDownload={onDownload}
                                    onPreview={onPreview}
                                    onPipelineSelect={onSelectFilePipeline}
                                />
                            </TableCell>
                            <TableCell>
                                {session.hasAudit &&
                                    <Icon
                                        name='file-text'
                                        button
                                        size='sm'
                                        onClick={() => onOpenAuditModal(session.sessionId)}
                                    />}
                            </TableCell>
                            <TableCell>
                                <SessionResultInfo session={session} onOpenNotesModal={onOpenNotesModal} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

        </div>
    );
};

